import { Clock, MapPin, Navigation } from "lucide-react";
import { Reveal } from "./Reveal";
import { Divider } from "./Ornaments";
import { CHOWKI, MATA_RANI_IMG, WEDDING, WEDDING_OPERA_IMG } from "./data";

const DirectionsButton = ({ href, testid, tone }) => (
    <a
        data-testid={testid}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`group mt-8 inline-flex items-center gap-3 rounded-full px-8 py-3.5 font-cinzel text-[11px] font-bold uppercase tracking-[0.28em] transition-all duration-500 hover:scale-[1.04] ${
            tone === "crimson"
                ? "bg-gradient-to-r from-burgundy to-[#5C1420] text-[#F5E9C8] ring-1 ring-gold/50"
                : "bg-gold text-obsidian"
        }`}
    >
        <Navigation size={14} className="transition-transform duration-500 group-hover:rotate-12" />
        Get Directions
    </a>
);

const AccentLine = ({ accent }) => (
    <span
        className={`absolute inset-x-10 bottom-0 h-px bg-gradient-to-r from-transparent to-transparent ${
            accent === "crimson" ? "via-gold/70" : "via-gold"
        }`}
    />
);

const EventShell = ({ children, image, imageAlt = "", testid, accent, contain = false }) => (
    <Reveal>
        <article
            data-testid={testid}
            className="group relative overflow-hidden rounded-2xl border border-gold/20 bg-royal-card/70 transition-colors duration-700 hover:border-gold/50"
        >
            {contain ? (
                <div className="relative flex justify-center bg-royal-surface/70 px-6 pt-8">
                    <img
                        src={image}
                        alt={imageAlt}
                        data-testid={`${testid}-header-image`}
                        className="h-44 w-auto rounded-lg object-contain drop-shadow-[0_6px_18px_rgba(184,134,11,0.18)] transition-transform duration-700 group-hover:scale-105 sm:h-52"
                    />
                    <AccentLine accent={accent} />
                </div>
            ) : (
                <div className="relative h-60 overflow-hidden sm:h-72">
                    <img
                        src={image}
                        alt={imageAlt}
                        data-testid={`${testid}-header-image`}
                        className="h-full w-full object-cover opacity-85 transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-royal-card via-royal-card/30 to-transparent" />
                    <AccentLine accent={accent} />
                </div>
            )}
            <div className="relative px-6 pb-10 pt-2 text-center sm:px-10">{children}</div>
        </article>
    </Reveal>
);

const Events = () => (
    <section
        id="celebrations"
        data-testid="events-section"
        className="relative py-24 sm:py-36"
    >
        <div className="mx-auto max-w-6xl px-6">
            <Reveal>
                <p className="text-center font-cinzel text-[11px] font-semibold uppercase tracking-[0.5em] text-gold">
                    The Celebrations
                </p>
            </Reveal>
            <Reveal delay={0.1}>
                <h2 className="mt-6 text-center font-royal text-3xl font-medium text-ivory sm:text-4xl">
                    Two Evenings of Blessings &amp; Joy
                </h2>
            </Reveal>
            <Reveal delay={0.15}>
                <Divider className="mt-8" />
            </Reveal>

            <div className="mt-16 grid gap-10 lg:grid-cols-2 lg:gap-12">
                <EventShell
                    image={MATA_RANI_IMG}
                    imageAlt="Mata Rani"
                    testid="event-card-chowki"
                    accent="crimson"
                    contain
                >
                    <p className="font-devanagari text-lg text-gold">{CHOWKI.invocation}</p>
                    <h3 className="gold-shimmer animate-shimmer-sweep mt-3 font-script text-5xl sm:text-6xl">
                        {CHOWKI.title}
                    </h3>
                    <p className="mt-5 font-cinzel text-xs font-semibold uppercase tracking-[0.3em] text-ivory sm:text-sm">
                        {CHOWKI.date}
                    </p>
                    <p className="mt-3 flex items-center justify-center gap-2 font-royal text-lg italic text-sand">
                        <Clock size={16} className="text-gold" /> {CHOWKI.time}
                    </p>
                    <p className="mt-1 font-royal text-base italic text-mutedgold">{CHOWKI.after}</p>
                    <p className="mx-auto mt-6 max-w-sm font-cinzel text-[11px] uppercase leading-relaxed tracking-[0.22em] text-sand">
                        <MapPin size={13} className="mr-1.5 inline text-gold" />
                        {CHOWKI.venue}
                        <span className="mt-1 block text-mutedgold">{CHOWKI.address}</span>
                    </p>
                    <DirectionsButton
                        href={CHOWKI.mapsUrl}
                        testid="directions-button-chowki"
                        tone="crimson"
                    />
                </EventShell>

                <EventShell
                    image={WEDDING_OPERA_IMG}
                    imageAlt="Wedding Opera banquet hall, Delhi"
                    testid="event-card-wedding"
                    accent="gold"
                >
                    <p className="font-devanagari text-lg text-gold">{WEDDING.invocation}</p>
                    <h3 className="gold-shimmer animate-shimmer-sweep mt-3 font-script text-5xl sm:text-6xl">
                        {WEDDING.title}
                    </h3>
                    <p className="mt-5 font-cinzel text-xs font-semibold uppercase tracking-[0.3em] text-ivory sm:text-sm">
                        {WEDDING.date}
                    </p>

                    <div className="mx-auto mt-8 max-w-md text-left">
                        {WEDDING.programme.map((item, i) => (
                            <div
                                key={item.name}
                                data-testid={`programme-item-${i}`}
                                className="relative flex gap-5 pb-7 last:pb-0"
                            >
                                {i < WEDDING.programme.length - 1 && (
                                    <span className="absolute left-[7px] top-5 h-full w-px bg-gradient-to-b from-gold/50 to-transparent" />
                                )}
                                <span className="mt-1.5 h-[15px] w-[15px] shrink-0 rotate-45 border border-gold bg-obsidian" />
                                <div>
                                    <p className="font-cinzel text-xs font-bold uppercase tracking-[0.25em] text-gold">
                                        {item.time} · {item.name}
                                    </p>
                                    <p className="mt-1 font-royal text-base italic text-sand">
                                        {item.location}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <DirectionsButton
                        href={WEDDING.mapsUrl}
                        testid="directions-button-wedding"
                        tone="gold"
                    />
                </EventShell>
            </div>
        </div>
    </section>
);

export default Events;
